import React, { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import { useDispatch, useSelector, RootStateOrAny } from "react-redux";
import { useParams } from "react-router-dom";
import { Spin, Tooltip, message, Divider } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import ReactSelect from "react-select";
import { v4 as uuidv4 } from "uuid";

import "./AddToCollection.scss";
import {
  handleBottomDrawer,
  handleBottomAddToCollection,
} from "../../actions/actionCreators";
import Folder from "../Folder/Folder";
import {
  getAllFolders,
  addProducttoFolder,
  createFolder,
  clearfolderToMoveTo,
  handleMovingState,
  handleMovingStateProducts,
  clearSelectedFolders,
  clearSelectedProducts,
  getOneFolder
} from "../../actions/folders-actions/actions";
import { getCollaborators } from "../../actions/order-actions/actions";
import { MyNotif } from "../NotifIcons/NotifIcons";
import { createCustomizedProduct } from "../../actions/order-actions/actions";
import PlusIcon from "../../icons/PlusIcon";

export interface IAddToCollectionProps {
  setMode: ((mode: string) => void) | undefined;
  mode?: string;
  addCustomizedProduct?: boolean;
  customizedOrder?: any;
}

export default function AddToCollection({
  setMode,
  mode,
  addCustomizedProduct,
  customizedOrder,
}: IAddToCollectionProps) {
  const dispatch = useDispatch();
  const folders = useSelector((state: RootStateOrAny) => state.folders);
  const drawers = useSelector((state: RootStateOrAny) => state.drawers);
  const { user } = useSelector((state: RootStateOrAny) => state.auth);
  const { collaborators } = useSelector((state: RootStateOrAny) => state.orders);
  const { saveCustomizedProductLoading } = useSelector(
    (state: RootStateOrAny) => state.orders
  );

  const [height, setHeight] = useState(window.innerHeight);
  const [showCollectionForm, setShowCollectionForm] = useState(false);
  const [folderData, setFolderData] = useState({
    name: "",
    description: "",
    collaborator: [],
    season: '',
    year: ""
  });

  // Animation variants
  const variants = {
    enter: { y: height },
    center: { y: 0 },
    exit: { y: height },
  };

  const variants2 = {
    enter: { y: 0 },
    center: { y: height },
    exit: { y: 0 },
  };

  useEffect(() => {
    dispatch(getAllFolders());
    dispatch(getCollaborators(""));
    
    const handleResize = () => setHeight(window.innerHeight);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [dispatch]);

  // Form options
  const currentYear = new Date().getFullYear();
  const options_year = useMemo(() => [
    { value: String(currentYear).slice(2), label: String(currentYear) },
    { value: String(currentYear + 1).slice(2), label: String(currentYear + 1) },
    { value: String(currentYear + 2).slice(2), label: String(currentYear + 2) },
    { value: String(currentYear + 3).slice(2), label: String(currentYear + 3) },
    { value: String(currentYear + 4).slice(2), label: String(currentYear + 4) },
  ], [currentYear]);

  const options_season = useMemo(() => [
    { value: 'ps', label: 'PS' },
    { value: 'ss', label: 'SS' },
    { value: 'pf', label: 'PF' },
    { value: 'fw', label: 'FW' }
  ], []);

  const customStylesCreateModern = {
    control: (base: any, state: any) => ({
      ...base,
      background: "white",
      borderColor: state.isFocused ? "#00468c" : "#e2e8f0",
      borderRadius: "8px",
      padding: "2px",
      fontSize: "14px",
      boxShadow: "none",
      "&:hover": {
        borderColor: "#00468c",
      },
    }),
    placeholder: (base: any) => ({
      ...base,
      color: "#94a3b8",
    }),
    menu: (base: any) => ({
      ...base,
      borderRadius: "12px",
      overflow: "hidden",
      boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
      border: "1px solid #e2e8f0",
      zIndex: 100
    }),
    option: (base: any, state: any) => ({
      ...base,
      backgroundColor: state.isSelected ? "#00468c" : state.isFocused ? "#f1f5f9" : "white",
      color: state.isSelected ? "white" : "#1e293b",
      padding: "10px 12px",
      fontSize: "14px",
      cursor: "pointer"
    }),
  };

  const handleCreateNewFolder = () => {
    if (!folderData.name) {
      message.error("Please specify the collection name!");
      return;
    }
    dispatch(createFolder({ ...folderData, product_id: folders.idProduct }));
    setShowCollectionForm(false);
    setFolderData({ name: "", description: "", collaborator: [], season: '', year: "" });
    message.success("Collection created successfully");
  };

  const handleConfirm = () => {
    if (addCustomizedProduct) {
      if (!folders.folderIdToMoveTo) {
        MyNotif("Please select a folder", "danger");
      } else {
        let formData = new FormData();
        Object.keys(customizedOrder)
          .filter((x: any) => !x.includes("files"))
          .map((el: any) =>
            customizedOrder[el] && formData.append(el, customizedOrder[el])
          );

        if (customizedOrder.style_files?.length) {
          for (let i = 0; i < customizedOrder.style_files.length; i++) {
            formData.append(`style_files[${i}]`, customizedOrder.style_files[i]);
          }
        }
        if (customizedOrder.fabric_files?.length) {
          for (let i = 0; i < customizedOrder.fabric_files.length; i++) {
            formData.append(`fabric_files[${i}]`, customizedOrder.fabric_files[i]);
          }
        }
        if (customizedOrder.color_files?.length) {
          for (let i = 0; i < customizedOrder.color_files.length; i++) {
            formData.append(`color_files[${i}]`, customizedOrder.color_files[i]);
          }
        }
        dispatch(createCustomizedProduct(formData, false));
      }
    } else if (folders.folderIdToMoveTo) {
      dispatch(addProducttoFolder(folders.folderIdToMoveTo, folders.idProduct));
      dispatch(getOneFolder(folders.folderIdToMoveTo));
      dispatch(handleBottomDrawer());
      dispatch(clearfolderToMoveTo());
    } else {
      message.error("Please select a collection first");
    }
  };

  const handleCancel = () => {
    if (drawers.openBottomMoveFolders) {
      dispatch(handleBottomAddToCollection());
      dispatch(clearSelectedFolders());
      dispatch(clearSelectedProducts());
      dispatch(clearfolderToMoveTo());
      dispatch(handleMovingStateProducts());
    } else {
      dispatch(handleBottomDrawer());
    }
  };

  return (
    <motion.div
      className="add_to_collection_container"
      transition={{ type: "tween" }}
      variants={mode === "add_to_collection" ? variants2 : variants}
      initial="enter"
      animate="center"
      exit="exit"
    >
      <div className="add_to_collection_modern_inner" style={{ width: "100%", maxWidth: "800px", margin: "0 auto" }}>
        {showCollectionForm ? (
          <div className="create_collection_form_modern" style={{ 
            padding: "24px", 
            borderRadius: "16px",
            display: "flex",
            flexDirection: "column",
            gap: "20px"
          }}>
            <h3 style={{ fontSize: "18px", fontWeight: 700, color: "#1e293b", margin: 0 }}>New Collection</h3>
            
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <label style={{ fontSize: "13px", fontWeight: 500, color: "#475569" }}>Name</label>
              <input
                type="text"
                placeholder="e.g. Summer 2027"
                value={folderData.name}
                onChange={(e) => setFolderData({ ...folderData, name: e.target.value })}
                style={{ 
                  border: "1px solid #e2e8f0", 
                  borderRadius: "8px", 
                  width: "100%", 
                  outline: "none", 
                  fontSize: "14px", 
                  padding: "10px 14px",
                  background: "white",
                  transition: "border-color 0.2s"
                }}
              />
            </div>

            <div style={{ display: "flex", gap: "16px" }}>
              <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "6px" }}>
                <label style={{ fontSize: "13px", fontWeight: 500, color: "#475569" }}>Season</label>
                <ReactSelect
                  placeholder="Select Season"
                  options={options_season}
                  value={options_season.find(o => o.value === folderData.season)}
                  onChange={(val: any) => setFolderData({ ...folderData, season: val.value })}
                  styles={customStylesCreateModern}
                />
              </div>
              <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "6px" }}>
                <label style={{ fontSize: "13px", fontWeight: 500, color: "#475569" }}>Year</label>
                <ReactSelect
                  placeholder="Select Year"
                  options={options_year}
                  value={options_year.find(o => o.value === folderData.year)}
                  onChange={(val: any) => setFolderData({ ...folderData, year: val.value })}
                  styles={customStylesCreateModern}
                />
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <label style={{ fontSize: "13px", fontWeight: 500, color: "#475569" }}>Collaborators</label>
              <ReactSelect
                isMulti
                placeholder="Search names..."
                options={collaborators?.map((c: any) => ({ label: c.name, value: c.id }))}
                onChange={(val: any) => setFolderData({ ...folderData, collaborator: val.map((v: any) => v.value) })}
                styles={customStylesCreateModern}
              />
            </div>

            <div className="buttons_grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginTop: "8px" }}>
              <button 
                className="btn_modern_cancel"
                onClick={() => setShowCollectionForm(false)}
                style={{
                  padding: "12px",
                  borderRadius: "10px",
                  border: "1px solid #e1e8f0",
                  background: "white",
                  fontSize: "14px",
                  fontWeight: 600,
                  cursor: "pointer"
                }}
              >
                Cancel
              </button>
              <button 
                className="btn_modern_confirm"
                onClick={handleCreateNewFolder}
                style={{
                  padding: "12px",
                  borderRadius: "10px",
                  border: "none",
                  background: "#00468c",
                  color: "white",
                  fontSize: "14px",
                  fontWeight: 600,
                  cursor: "pointer"
                }}
              >
                Create
              </button>
            </div>
          </div>
        ) : (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
              <span style={{ fontSize: "14px", fontWeight: 600, color: "#64748b" }}>Choose a collection</span>
              <Tooltip title="Create New Collection">
                <button
                  onClick={() => setShowCollectionForm(true)}
                  style={{ 
                    width: "36px", 
                    height: "36px", 
                    borderRadius: "50%", 
                    border: "1px solid #e1e8f0", 
                    background: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    color: "#00468c",
                    transition: "all 0.2s"
                  }}
                >
                  <PlusIcon />
                </button>
              </Tooltip>
            </div>

            <div className="list_folders" style={{ 
              display: "flex", 
              flexDirection: "row", 
              flexWrap: "wrap",
              gap: "12px", 
              maxHeight: "450px", 
              overflowY: "auto", 
              padding: "10px" 
            }}>
              {folders.folders &&
                folders.folders
                  .filter((el: any) => el.client_id == user?.id)
                  .map((folder: any) => (
                    <Folder
                      clickable={true}
                      selectOneFolder={true}
                      addToCollection={true}
                      folder={folder}
                      key={folder.id}
                    />
                  ))}
            </div>

            <div className="buttons_grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginTop: "20px" }}>
              <button 
                className="btn_modern_cancel"
                onClick={handleCancel}
                style={{
                  padding: "10px",
                  borderRadius: "8px",
                  border: "1px solid #e1e8f0",
                  background: "white",
                  fontSize: "14px",
                  fontWeight: 600,
                  cursor: "pointer"
                }}
              >
                Cancel
              </button>
              <button 
                className="btn_modern_confirm"
                onClick={handleConfirm}
                disabled={saveCustomizedProductLoading}
                style={{
                  padding: "10px",
                  borderRadius: "8px",
                  border: "none",
                  background: "#00468c",
                  color: "white",
                  fontSize: "14px",
                  fontWeight: 600,
                  cursor: "pointer",
                  opacity: saveCustomizedProductLoading ? 0.7 : 1
                }}
              >
                {saveCustomizedProductLoading ? <Spin indicator={<LoadingOutlined style={{ color: "#fff" }} spin />} /> : "Confirm"}
              </button>
            </div>
          </>
        )}
      </div>
    </motion.div>
  );
}
